// `formatFullDate` lives in `@stopal/shared/time` — the API renders the same
// format into customer-facing copy, so there is one definition rather than one
// per side. Re-exported so its existing importers keep one import path.
import { formatFullDate, localCalendarDate } from '@stopal/shared/time'

export { formatFullDate }

/**
 * The date to show for an order row: the day it is being delivered, or the day
 * it was placed when no delivery is booked yet.
 *
 * The two arguments are different KINDS of value and that is the whole point.
 * `deliveryDate` is a Postgres `date` — a bare `YYYY-MM-DD` naming a calendar
 * day, with no instant and no zone in it — so `new Date(deliveryDate)` parses
 * it as midnight UTC and renders the day BEFORE in every zone west of
 * Greenwich, which is every zone Stopal operates in. `formatFullDate` is the
 * repo's one local-midnight anchor for that shape.
 *
 * `createdAt` IS an instant — superjson revives it as a real `Date`, which is
 * why this takes one rather than a string — so it must NOT be parsed that way;
 * it is reduced to the viewer's own calendar day first. Rendering both through
 * one formatter is what keeps a single list from showing two date formats — and
 * what makes this one function rather than a ternary copied into each list,
 * which is how the bug came to exist in two places and would have been fixed in
 * one.
 */
export function formatOrderDate(
  deliveryDate: string | null | undefined,
  createdAt: Date,
): string {
  if (deliveryDate) return formatFullDate(deliveryDate)
  return formatFullDate(localCalendarDate(createdAt))
}

export function formatSmartDate(date: Date | string, now = new Date()): string {
  const d = date instanceof Date ? date : new Date(date)

  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()

  if (isToday) {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  const isThisYear = d.getFullYear() === now.getFullYear()

  if (isThisYear) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
